// frontend/src/widgets/WeatherSpotWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-03-04
// Updated:      2026-09-09 16:04 BST
//
// Description:  Met Office Global Spot Weather widget.
//               - Location pinning via LocationEditModal (DO NOT TOUCH).
//               - Fetches /api/weather/spot for:
//                   • Current card: ALWAYS hourly
//                   • Extended strip: hourly / three-hourly / daily
//               - Reads/writes shared pinned location so Weather and Flood widgets stay in sync.
//               - Resolves created locations robustly from modal callback payload or by GET /api/locations/{id}.
//
// Mobile + Performance Pass (2026-09-09):
//               - Preserves all /api/weather/spot periods, parameters, shared-location
//                 behaviour, Cache Bust semantics and LocationEditModal workflow.
//               - Removes overlapping first-mount request paths so each required weather
//                 dataset is requested once per settled coordinate/date/period key.
//               - Adds 90-second session caching keyed by location + period + date options
//                 so repeat mounts can render immediately without repeating identical calls.
//               - Adds in-flight de-duplication and stale-response suppression for Current,
//                 Period and Weekly requests.
//               - Manual Refresh always performs real network requests.
//               - Cache Bust clears matching browser caches and still sends cache_bust=1.
//               - Lazy-loads LocationEditModal only when the map picker is opened.
//               - Mobile stacks location/period controls and weather summary panels.
//               - Extended forecast becomes one complete forecast card per horizontal snap.
//               - Outer mobile widget grows to the complete natural workflow height using
//                 the proven two-pass width-matched fixed-host pattern.
//               - No duplicate mobile render tree is mounted.
//
// Operational Notes:
//               - First uncached requests still depend on backend/provider latency.
//               - No backend route, response shape, period meaning or report data transform changes.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import api from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext.jsx";

const LazyLocationEditModal = React.lazy(() =>
  import("../components/LocationEditModal.jsx")
);

const SHARED_LOCATION_KEY = "jobsPinnedLocation";
const SHARED_LOCATION_EVENT = "jobs-pinned-location-changed";

const WEATHER_SESSION_CACHE_PREFIX = "weatherSpot:v1:";
const WEATHER_SESSION_CACHE_TTL_MS = 90 * 1000;

const weatherCacheKey = ({ lat, lon, period, provider = "", startDate = "", days = "" }) => {
  const latNum = Number(lat);
  const lonNum = Number(lon);
  if (!Number.isFinite(latNum) || !Number.isFinite(lonNum)) return "";

  return [
    WEATHER_SESSION_CACHE_PREFIX,
    latNum.toFixed(6),
    lonNum.toFixed(6),
    String(period || ""),
    String(provider || ""),
    String(startDate || ""),
    String(days || ""),
  ].join("|");
};

const readWeatherSessionCache = (key) => {
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const savedAt = Number(parsed?.saved_at || 0);

    if (
      !savedAt ||
      Date.now() - savedAt > WEATHER_SESSION_CACHE_TTL_MS ||
      !parsed?.data
    ) {
      sessionStorage.removeItem(key);
      return null;
    }

    return parsed.data;
  } catch {
    return null;
  }
};

const writeWeatherSessionCache = (key, data) => {
  if (!key || !data) return;
  try {
    sessionStorage.setItem(
      key,
      JSON.stringify({
        saved_at: Date.now(),
        data,
      })
    );
  } catch {
    // Ignore quota/storage failures; network behaviour remains available.
  }
};

const clearWeatherSessionCache = (key) => {
  if (!key) return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
};

// ---------- Icons (local SVG pack) ----------
const iconsCtx = require.context(
  "../assets/weather-icons/weather-icons-master/design/fill/animation-ready",
  false,
  /\.svg$/
);

const iconSrc = (name) => {
  const safe = String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\-]/g, "");

  const file = safe.endsWith(".svg") ? safe : `${safe}.svg`;

  try {
    const mod = iconsCtx(`./${file}`);
    return mod?.default || mod;
  } catch {
    try {
      const mod = iconsCtx("./not-available.svg");
      return mod?.default || mod;
    } catch {
      return null;
    }
  }
};

// ---------- Shared pinned location ----------
const readSharedLocation = () => {
  try {
    const raw = localStorage.getItem(SHARED_LOCATION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const lat = Number(parsed?.lat);
    const lon = Number(parsed?.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    return {
      name: parsed?.name || "Pinned Location",
      lat,
      lon,
    };
  } catch {
    return null;
  }
};

const broadcastSharedLocation = (loc) => {
  window.dispatchEvent(
    new CustomEvent(SHARED_LOCATION_EVENT, {
      detail: {
        name: loc?.name || "Pinned Location",
        lat: Number(loc?.lat),
        lon: Number(loc?.lon),
        updated_at: new Date().toISOString(),
      },
    })
  );
};

const writeSharedLocation = (loc) => {
  const payload = {
    name: loc?.name || "Pinned Location",
    lat: Number(loc?.lat),
    lon: Number(loc?.lon),
    updated_at: new Date().toISOString(),
  };

  try {
    localStorage.setItem(SHARED_LOCATION_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }

  broadcastSharedLocation(payload);
};

const sameLocation = (a, b) =>
  Number(a?.lat) === Number(b?.lat) &&
  Number(a?.lon) === Number(b?.lon) &&
  String(a?.name || "") === String(b?.name || "");

const isFiniteNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n);
};

const extractLocationFromAny = (obj) => {
  if (!obj || typeof obj !== "object") return null;

  const lat =
    obj?.lat ??
    obj?.latitude ??
    obj?.location?.lat ??
    obj?.location?.latitude ??
    null;

  const lon =
    obj?.lon ??
    obj?.lng ??
    obj?.longitude ??
    obj?.location?.lon ??
    obj?.location?.lng ??
    obj?.location?.longitude ??
    null;

  const name =
    obj?.name ??
    obj?.label ??
    obj?.title ??
    obj?.location?.name ??
    "Pinned Location";

  if (!isFiniteNumber(lat) || !isFiniteNumber(lon)) return null;

  return {
    name: String(name || "Pinned Location"),
    lat: Number(lat),
    lon: Number(lon),
  };
};

const resolveCreatedLocation = async (created) => {
  const direct = extractLocationFromAny(created);
  if (direct) return direct;

  const id = created?.id ?? created?.location_id ?? created?.location?.id ?? null;
  if (!id) return null;

  const r = await api.get(`/locations/${id}`);
  return extractLocationFromAny(r?.data);
};

// ---------- Formatting ----------
const fmtNum = (n, dp = 1) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  return x.toFixed(dp);
};

const toDateOnly = (d) => {
  if (!d) return null;
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return null;
  return new Date(Date.UTC(x.getUTCFullYear(), x.getUTCMonth(), x.getUTCDate()));
};

const ymd = (d) => {
  const x = toDateOnly(d);
  if (!x) return null;
  const yy = x.getUTCFullYear();
  const mm = String(x.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(x.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
};

const fmtDay = (d) => {
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return "";
  return x.toLocaleDateString("en-GB", { weekday: "short" });
};

const fmtDM = (d) => {
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return "";
  return x.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

const fmtHm = (d) => {
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return "";
  return x.toLocaleTimeString("en-GB", { hour: "numeric", minute: "2-digit" }).toLowerCase();
};

const degToCompass = (deg) => {
  const x = Number(deg);
  if (!Number.isFinite(x)) return "—";
  const dirs = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
  const idx = Math.round((x % 360) / 22.5) % 16;
  return dirs[idx];
};

const mpsToMph = (mps) => {
  const x = Number(mps);
  if (!Number.isFinite(x)) return null;
  return x * 2.2369362920544;
};

const fmtVisibility = (m) => {
  const x = Number(m);
  if (!Number.isFinite(x)) return "—";
  if (x >= 10000) return `${Math.round(x / 1000)} km`;
  if (x >= 1000) return `${(x / 1000).toFixed(1)} km`;
  return `${Math.round(x)} m`;
};

// ---------- Error formatting ----------
const formatApiError = (err) => {
  const status = err?.response?.status;
  const data = err?.response?.data;

  if (data == null) return status ? `${status} — Request failed` : err?.message || "Request failed";
  if (typeof data === "string") return status ? `${status} — ${data}` : data;

  const detail = data?.detail ?? data?.message ?? data;
  try {
    const text = typeof detail === "string" ? detail : JSON.stringify(detail);
    return status ? `${status} — ${text}` : text;
  } catch {
    return status ? `${status} — Error` : "Error";
  }
};

// ---------- Data normalisation ----------
const pickFirstPoint = (data) => {
  if (!data || typeof data !== "object") return null;
  if (Array.isArray(data.series) && data.series.length) return data.series[0];
  if (Array.isArray(data.points) && data.points.length) return data.points[0];
  if (Array.isArray(data.forecast) && data.forecast.length) return data.forecast[0];
  if (Array.isArray(data.items) && data.items.length) return data.items[0];
  if (Array.isArray(data.periods) && data.periods.length) {
    const p0 = data.periods[0];
    if (Array.isArray(p0?.points) && p0.points.length) return p0.points[0];
  }
  if (data.data && typeof data.data === "object") return pickFirstPoint(data.data);
  return null;
};

const extractPoints = (data) => {
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data.series)) return data.series;
  if (Array.isArray(data.points)) return data.points;
  if (Array.isArray(data.forecast)) return data.forecast;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.periods) && data.periods.length) {
    const out = [];
    for (const p of data.periods) {
      if (Array.isArray(p?.points)) out.push(...p.points);
    }
    return out;
  }
  if (data.data && typeof data.data === "object") return extractPoints(data.data);
  return [];
};

const pointTime = (p) =>
  p?.time ??
  p?.timestamp ??
  p?.valid_time ??
  p?.validTime ??
  p?.dt ??
  p?.date ??
  null;

const pickNum = (p, keys) => {
  for (const k of keys) {
    const v = p?.[k];
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

const pickText = (p, keys) => {
  for (const k of keys) {
    const v = p?.[k];
    if (v != null && String(v).trim() !== "") return String(v);
  }
  return null;
};

// ---------- Icon mapping ----------
const iconFromSymbolOrCode = ({ symbol, code, isNight }) => {
  const sym = String(symbol || "").toLowerCase();
  const c = code == null ? null : String(code).trim();

  if (sym.includes("clear") || sym.includes("sun")) return isNight ? "clear-night" : "clear-day";
  if (sym.includes("partly") || sym.includes("broken")) return isNight ? "partly-cloudy-night" : "partly-cloudy-day";
  if (sym.includes("cloud")) return isNight ? "overcast-night" : "overcast-day";
  if (sym.includes("overcast")) return isNight ? "overcast-night" : "overcast-day";
  if (sym.includes("fog") || sym.includes("mist") || sym.includes("haze")) return isNight ? "fog-night" : "fog-day";
  if (sym.includes("drizzle")) return "drizzle";
  if (sym.includes("sleet")) return "sleet";
  if (sym.includes("snow")) return "snow";
  if (sym.includes("hail")) return "hail";
  if (sym.includes("thunder") || sym.includes("storm")) return isNight ? "thunderstorms-night" : "thunderstorms-day";
  if (sym.includes("rain") || sym.includes("shower")) return "rain";
  if (sym.includes("wind")) return "wind";

  const codeNum = Number(c);
  if (Number.isFinite(codeNum)) {
    if (codeNum === 0) return isNight ? "clear-night" : "clear-day";
    if ([1, 2, 3].includes(codeNum)) return isNight ? "partly-cloudy-night" : "partly-cloudy-day";
    if ([4, 5].includes(codeNum)) return isNight ? "overcast-night" : "overcast-day";
    if ([6, 7, 8].includes(codeNum)) return isNight ? "fog-night" : "fog-day";
    if ([9, 10].includes(codeNum)) return "drizzle";
    if ([11, 12, 13, 14].includes(codeNum)) return "rain";
    if ([15, 16].includes(codeNum)) return "sleet";
    if ([17, 18].includes(codeNum)) return "hail";
    if ([19, 20, 21, 22].includes(codeNum)) return "snow";
    if ([23, 24, 25, 26, 27, 28].includes(codeNum)) return isNight ? "thunderstorms-night" : "thunderstorms-day";
  }

  return "not-available";
};

const iconWithVisibilityOverride = ({ symbol, code, isNight, visibilityM }) => {
  const vis = Number(visibilityM);
  if (Number.isFinite(vis) && vis <= 5000) return isNight ? "fog-night" : "fog-day";
  return iconFromSymbolOrCode({ symbol, code, isNight });
};

// ---------- Daily/weekly normalisation ----------
const buildDailyFromPoints = (points, startDateYmd, days = 7) => {
  const start = toDateOnly(startDateYmd);
  if (!start) return [];

  const startMs = start.getTime();
  const endMs = startMs + days * 24 * 60 * 60 * 1000;

  const padded = [];
  for (let i = 0; i < days; i += 1) {
    const d = new Date(startMs + i * 86400000);
    padded.push({
      date: ymd(d),
      min: null,
      max: null,
      icon: "not-available",
      windMph: null,
      gustMph: null,
      windDirDeg: null,
      windDirTxt: null,
      precipMm: null,
      visibilityM: null,
      count: 0,
    });
  }

  const looksDaily =
    Array.isArray(points) &&
    points.length &&
    (points.some((p) => p?.day_max_temp_c != null || p?.night_min_temp_c != null) ||
      points.some((p) => p?.dayMaxTempC != null || p?.nightMinTempC != null));

  if (looksDaily) {
    const byDay = new Map();
    for (const p of points) {
      const t = pointTime(p);
      if (!t) continue;
      const dt = new Date(t);
      if (Number.isNaN(dt.getTime())) continue;

      const day = new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()));
      const ms = day.getTime();
      if (ms < startMs || ms >= endMs) continue;

      const key = ymd(day);
      if (!key) continue;

      const max = pickNum(p, ["day_max_temp_c", "dayMaxTempC", "max_temp_c", "maxTempC", "temp_c"]);
      const min = pickNum(p, ["night_min_temp_c", "nightMinTempC", "min_temp_c", "minTempC"]);

      const windMps = pickNum(p, ["wind_speed_mps", "windSpeedMps"]);
      const gustMps = pickNum(p, ["wind_gust_mps", "windGustMps"]);
      const windMph = pickNum(p, ["wind_mph", "wind_speed_mph", "windSpeedMph"]) ?? mpsToMph(windMps);
      const gustMph = pickNum(p, ["gust_mph", "wind_gust_mph", "gustSpeedMph"]) ?? mpsToMph(gustMps);

      const windDirDeg = pickNum(p, ["wind_dir_deg", "windDirDeg", "wind_direction_deg", "wind_direction"]);
      const windDirTxt = pickText(p, ["wind_dir_compass", "windDirCompass", "wind_dir_text"]);

      const precipMm =
        pickNum(p, ["precip_total_mm", "precipTotalMm", "precip_mm", "precipitation_mm", "precip"]) ?? null;

      const visibilityM = pickNum(p, ["visibility_m", "visibilityM", "visibility"]);

      const code = pickText(p, ["weather_code", "weatherCode", "code", "wx_code"]);
      const symbol = pickText(p, ["symbol", "wx_symbol", "weather_symbol", "weatherSymbol", "icon"]);

      byDay.set(key, {
        date: key,
        min: Number.isFinite(min) ? min : null,
        max: Number.isFinite(max) ? max : null,
        icon: iconWithVisibilityOverride({ symbol, code, isNight: false, visibilityM }),
        windMph: Number.isFinite(windMph) ? windMph : null,
        gustMph: Number.isFinite(gustMph) ? gustMph : null,
        windDirDeg: Number.isFinite(windDirDeg) ? windDirDeg : null,
        windDirTxt: windDirTxt || null,
        precipMm: Number.isFinite(precipMm) ? precipMm : null,
        visibilityM: Number.isFinite(visibilityM) ? visibilityM : null,
        count: 1,
      });
    }

    return padded.map((slot) => byDay.get(slot.date) || slot);
  }

  const byDay = new Map();

  for (const p of points || []) {
    const t = pointTime(p);
    if (!t) continue;
    const dt = new Date(t);
    if (Number.isNaN(dt.getTime())) continue;

    const day = new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()));
    const ms = day.getTime();
    if (ms < startMs || ms >= endMs) continue;

    const key = ymd(day);
    if (!key) continue;

    const temp = pickNum(p, ["temp_c", "temperature_c", "temperature", "temp", "tempC", "temperatureC"]);
    const windMps = pickNum(p, ["wind_speed_mps", "windSpeedMps"]);
    const gustMps = pickNum(p, ["wind_gust_mps", "windGustMps"]);
    const windMph = pickNum(p, ["wind_mph", "wind_speed_mph", "windSpeedMph", "wind_speed"]) ?? mpsToMph(windMps);
    const gustMph = pickNum(p, ["gust_mph", "wind_gust_mph", "gustSpeedMph", "wind_gust"]) ?? mpsToMph(gustMps);
    const windDirDeg = pickNum(p, ["wind_dir_deg", "wind_direction_deg", "windDirDeg", "wind_direction"]);
    const precipMm =
      pickNum(p, ["precip_total_mm", "precipTotalMm", "precip_mm", "precipitation_mm", "precip"]) ?? null;
    const visibilityM = pickNum(p, ["visibility_m", "visibilityM", "visibility"]);
    const code = pickText(p, ["weather_code", "weatherCode", "code", "wx_code"]);
    const symbol = pickText(p, ["symbol", "wx_symbol", "weather_symbol", "weatherSymbol", "icon"]);

    const row = byDay.get(key) || {
      date: key,
      min: null,
      max: null,
      icon: null,
      code: null,
      symbol: null,
      windMph: null,
      gustMph: null,
      windDirDeg: null,
      precipMm: null,
      visibilityM: null,
      count: 0,
    };

    if (Number.isFinite(temp)) {
      row.min = row.min == null ? temp : Math.min(row.min, temp);
      row.max = row.max == null ? temp : Math.max(row.max, temp);
    }

    if (Number.isFinite(visibilityM)) {
      row.visibilityM = row.visibilityM == null ? visibilityM : Math.min(row.visibilityM, visibilityM);
    }

    row.code = row.code ?? code;
    row.symbol = row.symbol ?? symbol;
    row.icon = iconWithVisibilityOverride({ symbol: row.symbol, code: row.code, isNight: false, visibilityM: row.visibilityM });

    if (Number.isFinite(windMph)) row.windMph = row.windMph == null ? windMph : Math.max(row.windMph, windMph);
    if (Number.isFinite(gustMph)) row.gustMph = row.gustMph == null ? gustMph : Math.max(row.gustMph, gustMph);
    if (Number.isFinite(precipMm)) row.precipMm = row.precipMm == null ? precipMm : Math.max(row.precipMm, precipMm);
    if (Number.isFinite(windDirDeg)) row.windDirDeg = windDirDeg;

    row.count += 1;
    byDay.set(key, row);
  }

  const out = Array.from(byDay.values()).sort((a, b) => (a.date < b.date ? -1 : 1));
  return padded.map((slot) => out.find((x) => x.date === slot.date) || slot);
};

// ---------- Period strip ----------
const buildPeriodStrip = (points, maxItems = 12) => {
  const arr = (points || [])
    .map((p) => {
      const t = pointTime(p);
      const dt = t ? new Date(t) : null;
      const ms = dt && !Number.isNaN(dt.getTime()) ? dt.getTime() : null;

      const temp = pickNum(p, ["temp_c", "temperature_c", "temperature", "temp", "tempC", "temperatureC"]);
      const feels = pickNum(p, ["feels_like_c", "feelslike_c", "feelsLikeC", "feels_like", "feelsLike"]);

      const precipMm =
        pickNum(p, ["precip_total_mm", "precipTotalMm", "precip_mm", "precipitation_mm", "precip"]) ??
        pickNum(p, ["precip_rate_mmph", "precipRateMmph"]) ??
        null;

      const windMps = pickNum(p, ["wind_speed_mps", "windSpeedMps"]);
      const gustMps = pickNum(p, ["wind_gust_mps", "windGustMps"]);
      const windMph = pickNum(p, ["wind_mph", "wind_speed_mph", "windSpeedMph", "wind_speed"]) ?? mpsToMph(windMps);
      const gustMph = pickNum(p, ["gust_mph", "wind_gust_mph", "gustSpeedMph", "wind_gust"]) ?? mpsToMph(gustMps);

      const windDirDeg = pickNum(p, ["wind_dir_deg", "wind_direction_deg", "windDirDeg", "wind_direction"]);
      const windDirTxt = pickText(p, ["wind_dir_compass", "windDirCompass", "wind_dir_text"]) || null;

      const visibilityM = pickNum(p, ["visibility_m", "visibilityM", "visibility"]);

      const code = pickText(p, ["weather_code", "weatherCode", "code", "wx_code"]);
      const symbol = pickText(p, ["symbol", "wx_symbol", "weather_symbol", "weatherSymbol", "icon"]);

      return {
        ms,
        time: t || null,
        dt,
        temp,
        feels,
        precipMm,
        windMph: Number.isFinite(windMph) ? windMph : null,
        gustMph: Number.isFinite(gustMph) ? gustMph : null,
        windDirDeg: Number.isFinite(windDirDeg) ? windDirDeg : null,
        windDirTxt,
        visibilityM: Number.isFinite(visibilityM) ? visibilityM : null,
        code,
        symbol,
      };
    })
    .filter((x) => x.ms != null)
    .sort((a, b) => a.ms - b.ms);

  const nowMs = Date.now();
  const future = arr.filter((x) => x.ms >= nowMs - 5 * 60 * 1000);

  const base = (future.length ? future : arr).slice(0, maxItems);

  return base.map((x) => {
    const isNight = false;
    const icon = iconWithVisibilityOverride({ symbol: x.symbol, code: x.code, isNight, visibilityM: x.visibilityM });
    return { ...x, icon };
  });
};

const WeatherSpotWidget = ({ startDate = null }) => {
  const { theme } = useTheme();

  const widgetRootRef = useRef(null);
  const contentRef = useRef(null);
  const forecastViewportRef = useRef(null);
  const firstForecastCardRef = useRef(null);

  const requestSeqRef = useRef({
    current: 0,
    period: 0,
    weekly: 0,
  });
  const inFlightRef = useRef({
    current: "",
    period: "",
    weekly: "",
  });

  const [period, setPeriod] = useState("hourly");
  const [currentBusy, setCurrentBusy] = useState(false);
  const [currentError, setCurrentError] = useState("");
  const [currentResp, setCurrentResp] = useState(null);

  const [periodBusy, setPeriodBusy] = useState(false);
  const [periodError, setPeriodError] = useState("");
  const [periodResp, setPeriodResp] = useState(null);

  const [weeklyBusy, setWeeklyBusy] = useState(false);
  const [weeklyError, setWeeklyError] = useState("");
  const [weeklyResp, setWeeklyResp] = useState(null);

  const [openPicker, setOpenPicker] = useState(false);

  const [loc, setLoc] = useState(
    () =>
      readSharedLocation() || {
        name: "Pinned Location",
        lat: 51.5074,
        lon: -0.1278,
      }
  );

  const headerFont = useMemo(() => theme?.font_header || "var(--font-header, Arial)", [theme]);
  const bodyFont = useMemo(() => theme?.font_body || "var(--font-body, Arial)", [theme]);
  const accent = useMemo(() => theme?.accent_color || "var(--accent-color, #ff7a00)", [theme]);

  const iconGradFrom = useMemo(() => theme?.secondary_color || "#476480", [theme]);
  const iconGradTo = useMemo(() => theme?.button_action?.hover || "#fead76", [theme]);

  const btnBase = {
    border: "none",
    borderRadius: 999,
    padding: "10px 16px",
    fontWeight: 900,
    cursor: "pointer",
    fontFamily: bodyFont,
    transition: "transform 0.06s ease, filter 0.15s ease",
    userSelect: "none",
  };

  const mkBtnHandlers = (normal, hover, click) => ({
    onMouseEnter: (e) => {
      e.currentTarget.style.backgroundColor = hover || normal;
    },
    onMouseLeave: (e) => {
      e.currentTarget.style.backgroundColor = normal;
      e.currentTarget.style.transform = "translateY(0px)";
    },
    onMouseDown: (e) => {
      e.currentTarget.style.backgroundColor = click || hover || normal;
      e.currentTarget.style.transform = "translateY(1px)";
    },
    onMouseUp: (e) => {
      e.currentTarget.style.backgroundColor = hover || normal;
      e.currentTarget.style.transform = "translateY(0px)";
    },
  });

  const actionBtnStyle = useMemo(() => {
    const normal = theme?.button_action?.normal || "#1f7a8c";
    return { ...btnBase, backgroundColor: normal, color: "#fff" };
  }, [theme, bodyFont]);

  const generalBtnStyle = useMemo(() => {
    const normal = theme?.button_general?.normal || "#222";
    return { ...btnBase, backgroundColor: normal, color: "#fff" };
  }, [theme, bodyFont]);

  const actionHandlers = useMemo(
    () =>
      mkBtnHandlers(
        theme?.button_action?.normal || "#1f7a8c",
        theme?.button_action?.hover || "#256f7f",
        theme?.button_action?.click || "#1b5e6c"
      ),
    [theme]
  );

  const generalHandlers = useMemo(
    () =>
      mkBtnHandlers(
        theme?.button_general?.normal || "#222",
        theme?.button_general?.hover || "#333",
        theme?.button_general?.click || "#111"
      ),
    [theme]
  );

  const inputStyle = useMemo(
    () => ({
      height: 44,
      padding: "10px 14px",
      border: "1px solid rgba(0,0,0,0.12)",
      borderRadius: 16,
      width: "100%",
      background: "#fff",
      boxSizing: "border-box",
      fontFamily: bodyFont,
      fontSize: 14,
      outline: "none",
    }),
    [bodyFont]
  );

  const selectStyle = useMemo(
    () => ({
      ...inputStyle,
      appearance: "none",
      WebkitAppearance: "none",
      MozAppearance: "none",
      paddingRight: 42,
      backgroundImage:
        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='rgba(0,0,0,0.55)'><path d='M7 10l5 5 5-5z'/></svg>\")",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right 14px center",
    }),
    [inputStyle]
  );

  const backendPeriod = period;

  const baseDateYmd = useMemo(() => {
    const fromProp = ymd(startDate);
    const fromResp = ymd(currentResp?.job_start_date || currentResp?.jobStartDate);
    return fromProp || fromResp || ymd(new Date());
  }, [startDate, currentResp]);

  const pinnedLabel = useMemo(() => {
    if (loc?.name && String(loc.name).trim()) return String(loc.name).trim();

    const locationObj = currentResp?.location || weeklyResp?.location || periodResp?.location || null;
    const nearest =
      locationObj?.nearest_place ||
      locationObj?.nearestPlace ||
      locationObj?.town ||
      locationObj?.city ||
      locationObj?.label ||
      locationObj?.name ||
      null;

    return String(nearest || "Pinned Location");
  }, [loc, currentResp, weeklyResp, periodResp]);

  const fetchCurrentHourly = useCallback(
    async ({ cacheBust = false, allowSessionCache = false } = {}) => {
      const key = weatherCacheKey({
        lat: loc.lat,
        lon: loc.lon,
        period: "hourly",
      });

      if (!key) return;

      if (allowSessionCache && !cacheBust) {
        const cached = readWeatherSessionCache(key);
        if (cached) {
          setCurrentResp(cached);
          setCurrentError("");
          setCurrentBusy(false);
          return;
        }
      }

      if (!cacheBust && inFlightRef.current.current === key) {
        return;
      }

      if (cacheBust) {
        clearWeatherSessionCache(key);
      }

      const seq = requestSeqRef.current.current + 1;
      requestSeqRef.current.current = seq;
      inFlightRef.current.current = key;

      setCurrentBusy(true);
      setCurrentError("");

      try {
        const r = await api.get("/weather/spot", {
          params: {
            lat: loc.lat,
            lon: loc.lon,
            period: "hourly",
            ...(cacheBust ? { cache_bust: 1 } : {}),
          },
        });

        if (seq !== requestSeqRef.current.current) return;

        const data = r?.data || null;
        setCurrentResp(data);
        if (data) writeWeatherSessionCache(key, data);
      } catch (e) {
        if (seq !== requestSeqRef.current.current) return;
        setCurrentResp(null);
        setCurrentError(formatApiError(e));
      } finally {
        if (seq === requestSeqRef.current.current) {
          inFlightRef.current.current = "";
          setCurrentBusy(false);
        }
      }
    },
    [loc.lat, loc.lon]
  );

  const fetchPeriodForecast = useCallback(
    async ({ cacheBust = false, allowSessionCache = false } = {}) => {
      if (period === "daily") return;

      const key = weatherCacheKey({
        lat: loc.lat,
        lon: loc.lon,
        period: backendPeriod,
      });

      if (!key) return;

      if (allowSessionCache && !cacheBust) {
        const cached = readWeatherSessionCache(key);
        if (cached) {
          setPeriodResp(cached);
          setPeriodError("");
          setPeriodBusy(false);
          return;
        }
      }

      if (!cacheBust && inFlightRef.current.period === key) {
        return;
      }

      if (cacheBust) {
        clearWeatherSessionCache(key);
      }

      const seq = requestSeqRef.current.period + 1;
      requestSeqRef.current.period = seq;
      inFlightRef.current.period = key;

      setPeriodBusy(true);
      setPeriodError("");

      try {
        const r = await api.get("/weather/spot", {
          params: {
            lat: loc.lat,
            lon: loc.lon,
            period: backendPeriod,
            ...(cacheBust ? { cache_bust: 1 } : {}),
          },
        });

        if (seq !== requestSeqRef.current.period) return;

        const data = r?.data || null;
        setPeriodResp(data);
        if (data) writeWeatherSessionCache(key, data);
      } catch (e) {
        if (seq !== requestSeqRef.current.period) return;
        setPeriodResp(null);
        setPeriodError(formatApiError(e));
      } finally {
        if (seq === requestSeqRef.current.period) {
          inFlightRef.current.period = "";
          setPeriodBusy(false);
        }
      }
    },
    [backendPeriod, loc.lat, loc.lon, period]
  );

  const fetchWeeklyDaily = useCallback(
    async ({ cacheBust = false, allowSessionCache = false } = {}) => {
      const key = weatherCacheKey({
        lat: loc.lat,
        lon: loc.lon,
        period: "daily",
        provider: "auto",
        startDate: baseDateYmd,
        days: 16,
      });

      if (!key) return;

      if (allowSessionCache && !cacheBust) {
        const cached = readWeatherSessionCache(key);
        if (cached) {
          setWeeklyResp(cached);
          setWeeklyError("");
          setWeeklyBusy(false);
          return;
        }
      }

      if (!cacheBust && inFlightRef.current.weekly === key) {
        return;
      }

      if (cacheBust) {
        clearWeatherSessionCache(key);
      }

      const seq = requestSeqRef.current.weekly + 1;
      requestSeqRef.current.weekly = seq;
      inFlightRef.current.weekly = key;

      setWeeklyBusy(true);
      setWeeklyError("");

      try {
        const r = await api.get("/weather/spot", {
          params: {
            lat: loc.lat,
            lon: loc.lon,
            period: "daily",
            provider: "auto",
            start_date: baseDateYmd,
            days: 16,
            ...(cacheBust ? { cache_bust: 1 } : {}),
          },
        });

        if (seq !== requestSeqRef.current.weekly) return;

        const data = r?.data || null;
        setWeeklyResp(data);
        if (data) writeWeatherSessionCache(key, data);
      } catch (e) {
        if (seq !== requestSeqRef.current.weekly) return;
        setWeeklyResp(null);
        setWeeklyError(formatApiError(e));
      } finally {
        if (seq === requestSeqRef.current.weekly) {
          inFlightRef.current.weekly = "";
          setWeeklyBusy(false);
        }
      }
    },
    [baseDateYmd, loc.lat, loc.lon]
  );

  useEffect(() => {
    const shared = readSharedLocation();
    if (shared) {
      setLoc((prev) => (sameLocation(prev, shared) ? prev : shared));
    }

    const onStorage = (event) => {
      if (event.key !== SHARED_LOCATION_KEY) return;
      const sharedLoc = readSharedLocation();
      if (!sharedLoc) return;
      setLoc((prev) => (sameLocation(prev, sharedLoc) ? prev : sharedLoc));
    };

    const onSharedLocation = (event) => {
      const nextLoc = event?.detail;
      if (!nextLoc || !isFiniteNumber(nextLoc?.lat) || !isFiniteNumber(nextLoc?.lon)) return;
      setLoc((prev) => (sameLocation(prev, nextLoc) ? prev : nextLoc));
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener(SHARED_LOCATION_EVENT, onSharedLocation);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(SHARED_LOCATION_EVENT, onSharedLocation);
    };
  }, []);

  const currentRequestKey = useMemo(
    () =>
      weatherCacheKey({
        lat: loc.lat,
        lon: loc.lon,
        period: "hourly",
      }),
    [loc.lat, loc.lon]
  );

  const weeklyRequestKey = useMemo(
    () =>
      weatherCacheKey({
        lat: loc.lat,
        lon: loc.lon,
        period: "daily",
        provider: "auto",
        startDate: baseDateYmd,
        days: 16,
      }),
    [baseDateYmd, loc.lat, loc.lon]
  );

  const periodRequestKey = useMemo(
    () =>
      period === "daily"
        ? ""
        : weatherCacheKey({
            lat: loc.lat,
            lon: loc.lon,
            period: backendPeriod,
          }),
    [backendPeriod, loc.lat, loc.lon, period]
  );

  const lastCurrentKeyRef = useRef("");
  useEffect(() => {
    if (!currentRequestKey || currentRequestKey === lastCurrentKeyRef.current) return;
    lastCurrentKeyRef.current = currentRequestKey;
    fetchCurrentHourly({
      cacheBust: false,
      allowSessionCache: true,
    });
  }, [currentRequestKey, fetchCurrentHourly]);

  const lastWeeklyKeyRef = useRef("");
  useEffect(() => {
    if (!weeklyRequestKey || weeklyRequestKey === lastWeeklyKeyRef.current) return;
    lastWeeklyKeyRef.current = weeklyRequestKey;
    fetchWeeklyDaily({
      cacheBust: false,
      allowSessionCache: true,
    });
  }, [fetchWeeklyDaily, weeklyRequestKey]);

  const lastPeriodKeyRef = useRef("");
  useEffect(() => {
    if (period === "daily") {
      setPeriodResp(null);
      setPeriodError("");
      setPeriodBusy(false);
      return;
    }

    if (!periodRequestKey || periodRequestKey === lastPeriodKeyRef.current) return;
    lastPeriodKeyRef.current = periodRequestKey;
    fetchPeriodForecast({
      cacheBust: false,
      allowSessionCache: true,
    });
  }, [fetchPeriodForecast, period, periodRequestKey]);

  const currentFirst = useMemo(() => pickFirstPoint(currentResp), [currentResp]);

  const modelRun = useMemo(() => {
    const src = period === "daily" ? weeklyResp : periodResp;
    const v =
      currentResp?.model_run ||
      currentResp?.run ||
      currentResp?.modelRun ||
      src?.model_run ||
      src?.run ||
      src?.modelRun;
    return v == null ? "" : String(v);
  }, [currentResp, weeklyResp, periodResp, period]);

  const currentTemp = useMemo(
    () => pickNum(currentFirst, ["temp_c", "temperature_c", "temperature", "temp", "tempC", "temperatureC"]),
    [currentFirst]
  );

  const feelsLike = useMemo(
    () => pickNum(currentFirst, ["feels_like_c", "feelslike_c", "feelsLikeC", "feels_like", "feelsLike"]),
    [currentFirst]
  );

  const humidity = useMemo(
    () => pickNum(currentFirst, ["humidity_pct", "humidity", "humidityPercent", "humidity_pct_mean"]),
    [currentFirst]
  );

  const pressureHpa = useMemo(() => {
    const hpa = pickNum(currentFirst, ["pressure_hpa", "pressureHpa", "pressure"]);
    if (Number.isFinite(hpa)) return hpa;
    const pa = pickNum(currentFirst, ["pressure_pa", "pressurePa"]);
    if (Number.isFinite(pa)) return pa / 100.0;
    return null;
  }, [currentFirst]);

  const visibilityM = useMemo(
    () => pickNum(currentFirst, ["visibility_m", "visibilityM", "visibility"]),
    [currentFirst]
  );

  const windMph = useMemo(() => {
    const mph = pickNum(currentFirst, ["wind_mph", "wind_speed_mph", "windSpeedMph", "wind_speed"]);
    if (Number.isFinite(mph)) return mph;
    const mps = pickNum(currentFirst, ["wind_speed_mps", "windSpeedMps"]);
    return mpsToMph(mps);
  }, [currentFirst]);

  const gustMph = useMemo(() => {
    const mph = pickNum(currentFirst, ["gust_mph", "wind_gust_mph", "gustSpeedMph", "wind_gust"]);
    if (Number.isFinite(mph)) return mph;
    const mps = pickNum(currentFirst, ["wind_gust_mps", "windGustMps"]);
    return mpsToMph(mps);
  }, [currentFirst]);

  const windDirDeg = useMemo(
    () => pickNum(currentFirst, ["wind_dir_deg", "wind_direction_deg", "windDirDeg", "wind_direction"]),
    [currentFirst]
  );

  const windDirTxt = useMemo(
    () => pickText(currentFirst, ["wind_dir_compass", "windDirCompass", "wind_dir_text"]) || null,
    [currentFirst]
  );

  const precipMm = useMemo(() => {
    const total = pickNum(currentFirst, ["precip_total_mm", "precipTotalMm", "precip_mm", "precipitation_mm", "precip"]);
    if (Number.isFinite(total)) return total;
    const rate = pickNum(currentFirst, ["precip_rate_mmph", "precipRateMmph"]);
    if (Number.isFinite(rate)) return rate;
    return null;
  }, [currentFirst]);

  const wxCode = useMemo(
    () => pickText(currentFirst, ["weather_code", "weatherCode", "code", "wx_code"]),
    [currentFirst]
  );

  const wxSymbol = useMemo(
    () => pickText(currentFirst, ["symbol", "wx_symbol", "weather_symbol", "weatherSymbol", "icon"]),
    [currentFirst]
  );

  const isNight = useMemo(() => {
    const now = new Date();
    const h = now.getHours();
    return h < 6 || h >= 19;
  }, []);

  const currentIconName = useMemo(
    () =>
      iconWithVisibilityOverride({
        symbol: wxSymbol,
        code: wxCode,
        isNight,
        visibilityM,
      }),
    [wxSymbol, wxCode, isNight, visibilityM]
  );

  const currentIcon = useMemo(() => iconSrc(currentIconName), [currentIconName]);

  const weeklyDays = useMemo(() => {
    const pts = extractPoints(weeklyResp);
    return buildDailyFromPoints(pts, baseDateYmd, 7);
  }, [weeklyResp, baseDateYmd]);

  const periodStrip = useMemo(() => {
    if (period === "daily") return [];
    const pts = extractPoints(periodResp);
    const maxItems = period === "three-hourly" ? 10 : 12;
    return buildPeriodStrip(pts, maxItems);
  }, [period, periodResp]);

  const liveBadge = useMemo(() => {
    const ok = !currentBusy && !currentError && currentResp;
    const bg = ok ? "#e8f5e9" : currentError ? "#ffebee" : "#e3f2fd";
    const bd = ok ? "#c8e6c9" : currentError ? "#ffcdd2" : "#bbdefb";
    const fg = ok ? "#1b5e20" : currentError ? "#b71c1c" : "#0d47a1";
    const text = ok ? "Live" : currentError ? "Error" : currentBusy ? "Loading" : "Idle";
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 12px",
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 900,
          background: bg,
          border: `1px solid ${bd}`,
          color: fg,
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: 99, background: fg, display: "inline-block" }} />
        {text}
      </span>
    );
  }, [currentBusy, currentError, currentResp]);

  const currentWindDirLabel = useMemo(() => {
    if (windDirTxt) return windDirTxt;
    if (Number.isFinite(windDirDeg)) return degToCompass(windDirDeg);
    return "—";
  }, [windDirTxt, windDirDeg]);

  const extendedStatusText = useMemo(() => {
    if (period === "daily") {
      if (weeklyBusy) return "Loading week…";
      if (weeklyError) return weeklyError;
      return "";
    }
    if (periodBusy) return "Loading…";
    if (periodError) return periodError;
    return "";
  }, [period, weeklyBusy, weeklyError, periodBusy, periodError]);

  const refreshAll = ({ cacheBust = false } = {}) => {
    fetchCurrentHourly({
      cacheBust,
      allowSessionCache: false,
    });

    if (period === "daily") {
      fetchWeeklyDaily({
        cacheBust,
        allowSessionCache: false,
      });
    } else {
      fetchPeriodForecast({
        cacheBust,
        allowSessionCache: false,
      });
    }
  };

  const iconTileLarge = useMemo(
    () => ({
      width: 92,
      height: 92,
      borderRadius: 26,
      background: `linear-gradient(135deg, ${iconGradFrom}, ${iconGradTo})`,
      border: "1px solid rgba(0,0,0,0.08)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 10px 24px rgba(0,0,0,0.10)",
    }),
    [iconGradFrom, iconGradTo]
  );

  const iconTileSmall = useMemo(
    () => ({
      width: 40,
      height: 40,
      borderRadius: 14,
      background: `linear-gradient(135deg, ${iconGradFrom}, ${iconGradTo})`,
      border: "1px solid rgba(0,0,0,0.10)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 8px 16px rgba(0,0,0,0.10)",
    }),
    [iconGradFrom, iconGradTo]
  );

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const root = widgetRootRef.current;
    const content = contentRef.current;
    if (!root || !content) return undefined;

    const mediaQuery = window.matchMedia("(max-width: 767px)");
    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;
      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];
      let node = root.parentElement;
      let depth = 0;

      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();

        const widthMatches = Math.abs(rect.width - rootRect.width) < 90;
        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (widthMatches && hasFixedInlineHeight) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      return candidates.length ? candidates[0] : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      root.style.height = "";
      root.style.minHeight = "";
      content.style.height = "";
      content.style.minHeight = "";

      if (!mediaQuery.matches) return;

      animationFrame = requestAnimationFrame(() => {
        root.style.height = "auto";
        root.style.minHeight = "0px";
        content.style.height = "auto";
        content.style.minHeight = "0px";

        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();
          if (!host) return;

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();
          const wrapperChromeHeight = Math.max(0, rootRect.top - hostRect.top);

          const requiredHostHeight = Math.ceil(
            Math.max(
              host.scrollHeight,
              wrapperChromeHeight + rootRect.height + 8
            )
          );

          if (!Number.isFinite(requiredHostHeight) || requiredHostHeight <= 0) {
            return;
          }

          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyMobileSizing())
        : null;

    resizeObserver?.observe(content);

    const handleMediaChange = () => applyMobileSizing();
    mediaQuery.addEventListener?.("change", handleMediaChange);
    window.addEventListener("resize", applyMobileSizing);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      mediaQuery.removeEventListener?.("change", handleMediaChange);
      window.removeEventListener("resize", applyMobileSizing);

      restoreHost();
      root.style.height = "";
      root.style.minHeight = "";
      content.style.height = "";
      content.style.minHeight = "";
    };
  }, [
    currentBusy,
    currentError,
    currentResp,
    period,
    periodBusy,
    periodError,
    periodResp,
    weeklyBusy,
    weeklyError,
    weeklyResp,
  ]);

  return (
    <div
      ref={widgetRootRef}
      className="h-auto min-h-0 w-full overflow-visible md:h-full"
      style={{
        border: "1px solid rgba(0,0,0,0.10)",
        borderRadius: 16,
        background: "#fff",
        padding: 16,
        fontFamily: bodyFont,
        boxSizing: "border-box",
        minWidth: 0,
      }}
    >
      <style>{`
        @media (max-width: 767px) {
          .vx-weather-mobile-button {
            width: 100% !important;
            min-height: 44px;
            white-space: normal !important;
          }

          .vx-weather-forecast-viewport {
            overflow-x: auto !important;
            overflow-y: hidden !important;
            overscroll-behavior-x: contain;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            scroll-snap-type: x mandatory;
          }

          .vx-weather-forecast-viewport::-webkit-scrollbar {
            display: none;
          }

          .vx-weather-forecast-card {
            scroll-snap-align: start;
            scroll-snap-stop: always;
          }
        }
      `}</style>

      <div ref={contentRef} className="min-w-0">
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontFamily: headerFont, fontSize: 22, fontWeight: 900, color: accent, lineHeight: 1.1 }}>
            Met Office Weather
          </div>
          <div style={{ fontSize: 13, color: "rgba(0,0,0,0.55)", marginTop: 4 }}>
            Global Spot • Forecast by pinned location
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>{liveBadge}</div>
      </div>

      <div style={{ height: 1, background: "rgba(0,0,0,0.08)", marginTop: 14, marginBottom: 14 }} />

      <div
        style={{
          borderRadius: 18,
          background: "rgba(0,0,0,0.03)",
          border: "1px solid rgba(0,0,0,0.06)",
          padding: 14,
        }}
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_160px_160px_180px] md:items-center">
          <div>
            <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)", marginBottom: 6 }}>
              Pinned Location
            </div>
            <input style={inputStyle} value={pinnedLabel} readOnly disabled />
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)", marginBottom: 6 }}>
              Latitude
            </div>
            <input style={inputStyle} value={fmtNum(loc.lat, 6)} readOnly disabled />
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)", marginBottom: 6 }}>
              Longitude
            </div>
            <input style={inputStyle} value={fmtNum(loc.lon, 6)} readOnly disabled />
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)", marginBottom: 6 }}>
              Period
            </div>
            <select style={selectStyle} value={period} onChange={(e) => setPeriod(e.target.value)}>
              <option value="hourly">Hourly</option>
              <option value="three-hourly">Three-hourly</option>
              <option value="daily">Daily</option>
            </select>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
          <button type="button" className="vx-weather-mobile-button" style={generalBtnStyle} {...generalHandlers} onClick={() => setOpenPicker(true)}>
            Pick Location (Map)
          </button>

          <button
            type="button"
            className="vx-weather-mobile-button"
            style={actionBtnStyle}
            {...actionHandlers}
            onClick={() => refreshAll({ cacheBust: false })}
            disabled={currentBusy || periodBusy || weeklyBusy}
          >
            Refresh
          </button>

          <button
            type="button"
            className="vx-weather-mobile-button"
            style={actionBtnStyle}
            {...actionHandlers}
            onClick={() => refreshAll({ cacheBust: true })}
            disabled={currentBusy || periodBusy || weeklyBusy}
          >
            Cache Bust
          </button>

          <button
            type="button"
            className="vx-weather-mobile-button"
            style={actionBtnStyle}
            {...actionHandlers}
            onClick={() => fetchWeeklyDaily({ cacheBust: false })}
            disabled={weeklyBusy}
            title={`Load weekly strip from ${baseDateYmd}`}
          >
            Load Week
          </button>

          <div className="w-full md:ml-auto md:w-auto" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ fontSize: 12, color: "rgba(0,0,0,0.55)", fontWeight: 900 }}>
              Week commencing: <span style={{ color: "rgba(0,0,0,0.80)" }}>{baseDateYmd}</span>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          marginTop: 14,
          borderRadius: 22,
          border: "1px solid rgba(0,0,0,0.08)",
          background: "#fff",
          padding: 16,
          boxShadow: "0 8px 20px rgba(0,0,0,0.04)",
        }}
      >
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1.2fr_1fr] lg:items-center">
          <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center" style={{ gap: 14 }}>
            <div style={iconTileLarge} title={currentIconName}>
              {currentIcon ? (
                <img
                  src={currentIcon}
                  alt={currentIconName}
                  style={{
                    width: 84,
                    height: 84,
                    filter: "drop-shadow(0 8px 12px rgba(0,0,0,0.25))",
                  }}
                />
              ) : (
                <span style={{ fontWeight: 900, color: "rgba(255,255,255,0.85)" }}>—</span>
              )}
            </div>

            <div>
              <div style={{ fontSize: 13, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>Current Weather</div>

              <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginTop: 6 }}>
                <div style={{ fontSize: 56, fontWeight: 900, color: "rgba(0,0,0,0.82)", lineHeight: 1 }}>
                  {currentTemp == null ? "—" : Math.round(currentTemp)}
                  <span style={{ fontSize: 26, fontWeight: 900, color: "rgba(0,0,0,0.60)" }}>°</span>
                </div>

                <div style={{ fontSize: 14, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>
                  {feelsLike == null ? "" : `Feels like ${Math.round(feelsLike)}°`}
                </div>
              </div>

              <div style={{ fontSize: 13, color: "rgba(0,0,0,0.60)", marginTop: 6 }}>{pinnedLabel}</div>
            </div>
          </div>

          <div
            style={{
              borderRadius: 18,
              background: "rgba(0,0,0,0.03)",
              border: "1px solid rgba(0,0,0,0.06)",
              padding: 14,
            }}
          >
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <img src={iconSrc("wind")} alt="wind" style={{ width: 26, height: 26, opacity: 0.9 }} />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>Wind</div>
                  <div style={{ fontSize: 14, fontWeight: 900 }}>
                    {windMph == null ? "—" : `${Math.round(windMph)} mph`}
                    <span style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)", marginLeft: 8 }}>
                      {currentWindDirLabel}
                    </span>
                  </div>
                </div>

                <div style={{ marginLeft: "auto", width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <img
                    src={iconSrc("compass")}
                    alt="dir"
                    style={{
                      width: 30,
                      height: 30,
                      transform: `rotate(${Number.isFinite(windDirDeg) ? windDirDeg : 0}deg)`,
                      opacity: Number.isFinite(windDirDeg) ? 0.95 : 0.35,
                      transition: "transform 120ms ease",
                    }}
                    title={windDirDeg == null ? "—" : `${Math.round(windDirDeg)}°`}
                  />
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <img src={iconSrc("windsock")} alt="gust" style={{ width: 26, height: 26, opacity: 0.9 }} />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>Gust</div>
                  <div style={{ fontSize: 14, fontWeight: 900 }}>
                    {gustMph == null ? "—" : `${Math.round(gustMph)} mph`}
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <img src={iconSrc("humidity")} alt="humidity" style={{ width: 26, height: 26, opacity: 0.9 }} />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>Humidity</div>
                  <div style={{ fontSize: 14, fontWeight: 900 }}>
                    {humidity == null ? "—" : `${Math.round(humidity)}%`}
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <img src={iconSrc("barometer")} alt="pressure" style={{ width: 26, height: 26, opacity: 0.9 }} />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>Pressure</div>
                  <div style={{ fontSize: 14, fontWeight: 900 }}>
                    {pressureHpa == null ? "—" : `${Math.round(pressureHpa)} hPa`}
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <img src={iconSrc("raindrops")} alt="precip" style={{ width: 26, height: 26, opacity: 0.9 }} />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>Precip</div>
                  <div style={{ fontSize: 14, fontWeight: 900 }}>
                    {precipMm == null ? "—" : `${fmtNum(precipMm, 1)} mm`}
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <img src={iconSrc("fog-day")} alt="visibility" style={{ width: 26, height: 26, opacity: 0.9 }} />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>Visibility</div>
                  <div style={{ fontSize: 14, fontWeight: 900 }}>
                    {visibilityM == null ? "—" : fmtVisibility(visibilityM)}
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
              <div style={{ fontSize: 12, color: "rgba(0,0,0,0.55)", fontWeight: 900 }}>
                Model run:{" "}
                <span style={{ fontFamily: "monospace", color: "rgba(0,0,0,0.80)" }}>
                  {modelRun || "—"}
                </span>
              </div>
              <div style={{ fontSize: 12, color: "rgba(0,0,0,0.55)", fontWeight: 900 }}>
                Code: <span style={{ color: "rgba(0,0,0,0.80)" }}>{wxCode || "—"}</span>
              </div>
            </div>

            {!!currentError && (
              <div style={{ marginTop: 10, color: "#b71c1c", fontWeight: 900, fontSize: 12 }}>
                {currentError}
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
          <div style={{ fontSize: 13, fontWeight: 900, color: "rgba(0,0,0,0.60)" }}>Extended Forecast</div>
          <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.50)" }}>{extendedStatusText}</div>
        </div>

        <div
          ref={forecastViewportRef}
          className="vx-weather-forecast-viewport"
          style={{
            marginTop: 10,
            borderRadius: 22,
            background: "rgba(0,0,0,0.03)",
            border: "1px solid rgba(0,0,0,0.06)",
            padding: 12,
            overflowX: "auto",
          }}
        >
          {period === "daily" ? (
            <div className="grid grid-flow-col auto-cols-[minmax(calc(100vw-80px),1fr)] gap-2.5 sm:auto-cols-[minmax(180px,1fr)] md:auto-cols-auto md:grid-flow-row md:grid-cols-7">
              {weeklyDays.map((d, index) => {
                const icon = iconSrc(d.icon || "not-available");
                const dateObj = d?.date ? new Date(`${d.date}T00:00:00Z`) : null;
                const windDir = Number.isFinite(d.windDirDeg) ? d.windDirDeg : null;
                const windDirTxt = d.windDirTxt || (windDir == null ? "—" : degToCompass(windDir));
                const max = d.max == null ? null : Math.round(d.max);
                const min = d.min == null ? null : Math.round(d.min);

                return (
                  <div
                    key={d.date}
                    ref={index === 0 ? firstForecastCardRef : null}
                    className="vx-weather-forecast-card min-w-0"
                    style={{
                      borderRadius: 18,
                      background: "#fff",
                      border: "1px solid rgba(0,0,0,0.08)",
                      padding: 12,
                      boxShadow: "0 6px 16px rgba(0,0,0,0.04)",
                      minHeight: 140,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 900, color: "rgba(0,0,0,0.75)" }}>
                          {dateObj ? fmtDay(dateObj) : "—"}
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.45)" }}>
                          {dateObj ? fmtDM(dateObj) : ""}
                        </div>
                      </div>

                      <div style={iconTileSmall} title={d.icon || "not-available"}>
                        {icon ? (
                          <img
                            src={icon}
                            alt={d.icon}
                            style={{ width: 30, height: 30, filter: "drop-shadow(0 6px 10px rgba(0,0,0,0.25))" }}
                          />
                        ) : null}
                      </div>
                    </div>

                    <div style={{ marginTop: 10, display: "flex", alignItems: "baseline", gap: 6 }}>
                      <div style={{ fontSize: 20, fontWeight: 900, color: "rgba(0,0,0,0.80)" }}>
                        {max == null ? "—" : `${max}°`}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 900, color: "rgba(0,0,0,0.45)" }}>
                        {min == null ? "" : `/${min}°`}
                      </div>
                    </div>

                    <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <img src={iconSrc("wind")} alt="wind" style={{ width: 18, height: 18, opacity: 0.85 }} />
                        <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.65)" }}>
                          {d.windMph == null ? "—" : `${Math.round(d.windMph)} mph`}{" "}
                          <span style={{ color: "rgba(0,0,0,0.45)" }}>{windDirTxt}</span>
                        </div>
                        <div style={{ marginLeft: "auto", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <img
                            src={iconSrc("compass")}
                            alt="dir"
                            style={{
                              width: 16,
                              height: 16,
                              transform: `rotate(${windDir == null ? 0 : windDir}deg)`,
                              opacity: windDir == null ? 0.25 : 0.85,
                            }}
                          />
                        </div>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <img src={iconSrc("windsock")} alt="gust" style={{ width: 18, height: 18, opacity: 0.85 }} />
                        <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.65)" }}>
                          Gust {d.gustMph == null ? "—" : `${Math.round(d.gustMph)} mph`}
                        </div>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <img src={iconSrc("raindrops")} alt="precip" style={{ width: 18, height: 18, opacity: 0.85 }} />
                        <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.65)" }}>
                          Precip {d.precipMm == null ? "—" : `${fmtNum(d.precipMm, 1)} mm`}
                        </div>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <img src={iconSrc("fog-day")} alt="vis" style={{ width: 18, height: 18, opacity: 0.85 }} />
                        <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.65)" }}>
                          Vis {d.visibilityM == null ? "—" : fmtVisibility(d.visibilityM)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div
              className="grid grid-flow-col auto-cols-[minmax(calc(100vw-80px),1fr)] gap-2.5 sm:auto-cols-[minmax(180px,1fr)] md:auto-cols-[minmax(120px,1fr)]"
            >
              {(periodStrip.length ? periodStrip : new Array(period === "three-hourly" ? 10 : 12).fill(null)).map((p, idx) => {
                const icon = iconSrc(p?.icon || "not-available");
                const windDir = Number.isFinite(p?.windDirDeg) ? p.windDirDeg : null;
                const windDirTxt = p?.windDirTxt || (windDir == null ? "—" : degToCompass(windDir));
                const tLabel = p?.dt ? fmtHm(p.dt) : "—";
                const temp = p?.temp == null ? null : Math.round(p.temp);

                return (
                  <div
                    key={p?.ms || idx}
                    ref={idx === 0 ? firstForecastCardRef : null}
                    className="vx-weather-forecast-card min-w-0"
                    style={{
                      borderRadius: 18,
                      background: "#fff",
                      border: "1px solid rgba(0,0,0,0.08)",
                      padding: 12,
                      boxShadow: "0 6px 16px rgba(0,0,0,0.04)",
                      minHeight: 140,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 900, color: "rgba(0,0,0,0.75)" }}>{tLabel}</div>
                        <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.45)" }}>
                          {p?.dt ? fmtDM(p.dt) : ""}
                        </div>
                      </div>

                      <div style={iconTileSmall} title={p?.icon || "not-available"}>
                        {icon ? (
                          <img
                            src={icon}
                            alt={p?.icon || "na"}
                            style={{ width: 30, height: 30, filter: "drop-shadow(0 6px 10px rgba(0,0,0,0.25))" }}
                          />
                        ) : null}
                      </div>
                    </div>

                    <div style={{ marginTop: 10, display: "flex", alignItems: "baseline", gap: 6 }}>
                      <div style={{ fontSize: 20, fontWeight: 900, color: "rgba(0,0,0,0.80)" }}>
                        {temp == null ? "—" : `${temp}°`}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 900, color: "rgba(0,0,0,0.45)" }}>
                        {p?.feels == null ? "" : `feels ${Math.round(p.feels)}°`}
                      </div>
                    </div>

                    <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <img src={iconSrc("wind")} alt="wind" style={{ width: 18, height: 18, opacity: 0.85 }} />
                        <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.65)" }}>
                          {p?.windMph == null ? "—" : `${Math.round(p.windMph)} mph`}{" "}
                          <span style={{ color: "rgba(0,0,0,0.45)" }}>{windDirTxt}</span>
                        </div>
                        <div style={{ marginLeft: "auto", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <img
                            src={iconSrc("compass")}
                            alt="dir"
                            style={{
                              width: 16,
                              height: 16,
                              transform: `rotate(${windDir == null ? 0 : windDir}deg)`,
                              opacity: windDir == null ? 0.25 : 0.85,
                            }}
                          />
                        </div>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <img src={iconSrc("windsock")} alt="gust" style={{ width: 18, height: 18, opacity: 0.85 }} />
                        <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.65)" }}>
                          Gust {p?.gustMph == null ? "—" : `${Math.round(p.gustMph)} mph`}
                        </div>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <img src={iconSrc("raindrops")} alt="precip" style={{ width: 18, height: 18, opacity: 0.85 }} />
                        <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.65)" }}>
                          Precip {p?.precipMm == null ? "—" : `${fmtNum(p.precipMm, 1)} mm`}
                        </div>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <img src={iconSrc("fog-day")} alt="vis" style={{ width: 18, height: 18, opacity: 0.85 }} />
                        <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.65)" }}>
                          Vis {p?.visibilityM == null ? "—" : fmtVisibility(p.visibilityM)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      </div>

      {openPicker ? (
        <React.Suspense fallback={null}>
          <LazyLocationEditModal
            open={openPicker}
        onClose={() => setOpenPicker(false)}
        onCreated={async (created) => {
          try {
            const resolved = await resolveCreatedLocation(created);
            if (!resolved) {
              setOpenPicker(false);
              setCurrentError("Location was created, but coordinates could not be resolved from the response.");
              return;
            }
            setLoc(resolved);
            writeSharedLocation(resolved);
            setOpenPicker(false);
          } catch (e) {
            setOpenPicker(false);
            setCurrentError(formatApiError(e));
          }
        }}
            initial={{
              name: loc.name,
              lat: loc.lat,
              lon: loc.lon,
              timezone: "Europe/London",
              country: "United Kingdom",
            }}
          />
        </React.Suspense>
      ) : null}
    </div>
  );
};

export default WeatherSpotWidget;